import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import LotusLoader from "../components/LotusLoader";
import Particles from "../components/Particles";
import { ordersApi } from "../lib/api";

// 28 Indian states + 8 Union Territories
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  // Union Territories
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

function digitsOnly(v) {
  return (v || "").replace(/\D/g, "");
}

export default function DeliveryAddress() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    fullName: "",
    mobile: "",
    line1: "",
    line2: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    ordersApi
      .get(id)
      .then((o) => {
        setOrder(o);
        const existing = o.deliveryAddress || {};
        // Pre-fill from saved address if present, else from quiz data
        const phone = digitsOnly(existing.mobile || o.whatsapp || "");
        const mobile10 = phone.length > 10 ? phone.slice(-10) : phone;
        setForm({
          fullName: existing.fullName || o.fullName || "",
          mobile: mobile10,
          line1: existing.line1 || "",
          line2: existing.line2 || "",
          landmark: existing.landmark || "",
          city: existing.city || "",
          state: existing.state || "",
          pincode: existing.pincode || "",
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  const isValid = useMemo(() => {
    return (
      form.fullName.trim().length > 1 &&
      digitsOnly(form.mobile).length === 10 &&
      form.line1.trim().length > 2 &&
      form.city.trim().length > 1 &&
      form.state.trim().length > 1 &&
      /^\d{6}$/.test(form.pincode)
    );
  }, [form]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (form.fullName.trim().length < 2) e.fullName = "Please enter your full name";
    if (digitsOnly(form.mobile).length !== 10) e.mobile = "Enter a 10-digit Indian mobile number";
    if (form.line1.trim().length < 3) e.line1 = "House/flat no., building or street is required";
    if (form.city.trim().length < 2) e.city = "City is required";
    if (!form.state) e.state = "Please select a state";
    if (!/^\d{6}$/.test(form.pincode)) e.pincode = "PIN code must be exactly 6 digits";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      await ordersApi.saveAddress(id, {
        fullName: form.fullName.trim(),
        mobile: digitsOnly(form.mobile),
        line1: form.line1.trim(),
        line2: form.line2.trim(),
        landmark: form.landmark.trim(),
        city: form.city.trim(),
        state: form.state,
        pincode: form.pincode,
      });
      navigate(`/order/${id}/confirmation`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save address. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) return <LotusLoader context="general" />;
  if (!order) {
    return (
      <div className="min-h-screen">
        <BrandHeader />
        <div className="max-w-md mx-auto px-5 py-20 text-center">
          <h1 className="font-serif text-2xl text-[#2C0A5E]">Order not found</h1>
        </div>
      </div>
    );
  }

  const isResuming = order.addressStatus === "pending" && !!(order.deliveryAddress);
  const paymentDone = order.paymentStatus === "Payment Confirmed";

  return (
    <div className="min-h-screen" data-testid="delivery-address-page">
      <BrandHeader />
      <section className="relative overflow-hidden hero-mystical">
        <Particles variant="violet" />
        <div className="relative max-w-2xl mx-auto px-5 md:px-10 py-12 md:py-16 text-center">
          {paymentDone && (
            <div className="ph-overline" style={{ color: "#D4A855" }} data-testid="address-overline">
              {isResuming ? "Resume Your Delivery Details" : "Step 2 of 2"}
            </div>
          )}
          <h1 className="font-serif text-3xl md:text-5xl mt-3 text-white" data-testid="address-headline">
            {paymentDone ? "Your payment is confirmed! 🎉" : "Where should we deliver your prescription?"}
          </h1>
          <p className="font-serif italic text-base md:text-lg mt-4 max-w-xl mx-auto" style={{ color: "#FAF7F2" }} data-testid="address-subline">
            {paymentDone
              ? "One last step — tell us where to deliver your Sacred Crystal Prescription."
              : "Add your address so we can ship your Sacred Crystal Prescription."}
          </p>
          {order.orderNumber && (
            <p className="mt-3 text-xs tracking-[0.3em] uppercase" style={{ color: "#D4A855" }} data-testid="address-order-number">
              Order #{order.orderNumber}
            </p>
          )}
        </div>
      </section>

      <section className="bg-[#FAF7F2] py-10 md:py-14">
        <div className="max-w-2xl mx-auto px-5 md:px-10">
          <form onSubmit={handleSubmit} className="ph-card p-6 md:p-8 space-y-5" data-testid="address-form">
            <Field label="Full Name *" error={errors.fullName}>
              <input
                type="text"
                className="ph-input"
                value={form.fullName}
                onChange={(e) => setField("fullName", e.target.value)}
                placeholder="As you'd like it on the parcel"
                data-testid="address-fullname"
                autoComplete="name"
              />
            </Field>

            <Field label="Mobile Number * (10 digits, India)" error={errors.mobile}>
              <div className="flex items-stretch">
                <span
                  className="inline-flex items-center px-3 rounded-l-md text-sm font-medium"
                  style={{ background: "#EEEDFE", color: "#2C0A5E", border: "1px solid rgba(106,13,173,0.18)", borderRight: "none" }}
                >
                  +91
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  className="ph-input rounded-l-none"
                  value={form.mobile}
                  onChange={(e) => setField("mobile", digitsOnly(e.target.value).slice(0, 10))}
                  placeholder="98765 43210"
                  data-testid="address-mobile"
                  autoComplete="tel-national"
                />
              </div>
            </Field>

            <Field label="Address Line 1 *" error={errors.line1} hint="House/flat no., building, street">
              <input
                type="text"
                className="ph-input"
                value={form.line1}
                onChange={(e) => setField("line1", e.target.value)}
                placeholder="Flat 12, Lotus Apartments, MG Road"
                data-testid="address-line1"
                autoComplete="address-line1"
              />
            </Field>

            <Field label="Address Line 2 / Landmark" hint="Optional">
              <input
                type="text"
                className="ph-input"
                value={form.line2}
                onChange={(e) => setField("line2", e.target.value)}
                placeholder="Near City Mall"
                data-testid="address-line2"
                autoComplete="address-line2"
              />
            </Field>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="City *" error={errors.city}>
                <input
                  type="text"
                  className="ph-input"
                  value={form.city}
                  onChange={(e) => setField("city", e.target.value)}
                  placeholder="Bengaluru"
                  data-testid="address-city"
                  autoComplete="address-level2"
                />
              </Field>
              <Field label="State *" error={errors.state}>
                <select
                  className="ph-input"
                  value={form.state}
                  onChange={(e) => setField("state", e.target.value)}
                  data-testid="address-state"
                  autoComplete="address-level1"
                >
                  <option value="">Select state…</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="PIN Code * (6 digits)" error={errors.pincode}>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="ph-input"
                value={form.pincode}
                onChange={(e) => setField("pincode", digitsOnly(e.target.value).slice(0, 6))}
                placeholder="560001"
                data-testid="address-pincode"
                autoComplete="postal-code"
              />
            </Field>

            <button
              type="submit"
              disabled={!isValid || submitting}
              data-testid="address-submit"
              style={{
                width: "100%",
                padding: "16px 24px",
                borderRadius: "12px",
                background: isValid && !submitting ? "#6A0DAD" : "#B7A6CD",
                color: "#D4A855",
                fontWeight: 700,
                letterSpacing: "0.04em",
                fontSize: "16px",
                marginTop: "8px",
                cursor: isValid && !submitting ? "pointer" : "not-allowed",
                transition: "background-color 200ms ease, transform 200ms ease",
                boxShadow: isValid && !submitting ? "0 8px 24px rgba(106,13,173,0.35)" : "none",
              }}
            >
              {submitting ? "Saving…" : "Confirm My Order →"}
            </button>
            <p className="text-xs text-center text-[#6B5E7E]">
              We ship across India. You can update this address by WhatsApp within 24 hours.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#2C0A5E]">{label}</span>
      {hint && <span className="block text-xs text-[#6B5E7E] mt-0.5">{hint}</span>}
      <div className="mt-1.5">{children}</div>
      {error && <span className="block text-xs mt-1" style={{ color: "#C13B1B" }}>{error}</span>}
    </label>
  );
}
